import { flexRender, Table as ReactTable } from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Icons } from '../icons';

export interface DataTableProps<TData> {
  className?: string;
  table: ReactTable<TData>;
  isLoading?: boolean;
  onRowClick?: false | ((row: TData) => void);
}

const DataTable = <TData,>({
  className,
  table,
  isLoading,
  onRowClick,
}: DataTableProps<TData>) => (
  <Table className={className}>
    <TableHeader>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <TableHead key={header.id} {...header.column.columnDef.meta}>
              {header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
            </TableHead>
          ))}
        </TableRow>
      ))}
    </TableHeader>
    <TableBody>
      <DataTableBody
        table={table}
        isLoading={isLoading}
        onRowClick={onRowClick}
      />
    </TableBody>
  </Table>
);

interface IDataTableBodyProps<TData> {
  table: ReactTable<TData>;
  isLoading?: boolean;
  onRowClick?: false | ((row: TData) => void);
}

export const DataTableBody = <TData,>({
  table,
  isLoading,
  onRowClick,
}: IDataTableBodyProps<TData>) => {
  if (table.getRowModel().rows?.length) {
    return (
      <>
        {table.getRowModel().rows.map((row) => (
          <TableRow
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
            onClick={() => onRowClick && onRowClick(row.original)}
            className={cn('hover:bg-gray-50', {
              'cursor-pointer': Boolean(onRowClick),
            })}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id} {...cell.column.columnDef.meta}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </>
    );
  }

  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={table.getAllColumns().length} className="h-24">
          <div className="flex justify-center">
            <Icons.spinner className="h-6 w-6 animate-spin" />
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell
        colSpan={table.getAllColumns().length}
        className="h-24 text-center"
      >
        Sem resultados.
      </TableCell>
    </TableRow>
  );
};

export default DataTable;
